import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { MapPin, Phone, CheckCircle2, Calendar, ArrowRight } from 'lucide-react';
import SEO from '@/components/dubinko/SEO';
import { useSiteContent } from '@/hooks/useSiteContent';
import { AREAS } from '@/constants/testIds';

export default function ServiceAreaPage() {
  const { slug } = useParams();
  const { content } = useSiteContent();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/areas/${slug}`)
      .then(({ data }) => setArea(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="container-x py-20 text-slate-500">Učitavanje…</main>;
  if (notFound || !area) {
    return (
      <main className="container-x py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Područje nije pronađeno</h1>
        <Link to="/podrucja" className="btn-primary mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold">
          Sva područja
        </Link>
      </main>
    );
  }

  const seoTitle = area.seo_title || `Dubinsko čišćenje ${area.name}`;
  const seoDesc = area.seo_description || `${area.description} Pozovite ${content.phone} ili zakažite online.`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Dubinsko čišćenje — ${area.name}`,
    provider: { '@type': 'CleaningService', name: 'Dubinko', telephone: content.phone },
    areaServed: { '@type': 'Place', name: area.name },
    description: seoDesc,
  };

  return (
    <main data-testid={AREAS.page}>
      <SEO title={seoTitle} description={seoDesc} path={`/podrucja/${area.slug}`} schema={schema} />

      <section className="section-pad aurora-bg grain">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="tag-pill"><MapPin className="w-3.5 h-3.5" /> Područje · {area.name}</span>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-4">
              Dubinsko čišćenje na {area.name}
            </h1>
            <p className="text-slate-600 mt-5 text-lg leading-relaxed">{area.description}</p>
            {area.neighborhoods && (
              <p className="text-sm text-slate-500 mt-3"><strong>Pokrivamo:</strong> {area.neighborhoods}</p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={`/rezervacija`} className="btn-primary px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Zakaži za {area.name}
              </Link>
              <a href={`tel:${content.phone}`} className="btn-ghost px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {content.phone}
              </a>
            </div>
          </div>
          <div>
            <img src={area.hero_image || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80'}
                 alt={`Dubinsko čišćenje ${area.name}`} loading="lazy"
                 className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl" />
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            'Stanovi i kuće',
            'Poslovni prostori',
            'Tepisi i tapacirung',
            'Move-in / Move-out',
            'Posle renoviranja',
            'Apartmani i smeštaji',
          ].map((s) => (
            <div key={s} className="bg-white rounded-3xl border border-slate-200 p-6">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <h3 className="font-display text-lg font-bold mt-3">{s}</h3>
              <p className="text-slate-600 text-sm mt-1">Profesionalno čišćenje u području {area.name}.</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <Link to="/podrucja" className="text-blue-700 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all">
            Sva područja <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
